<template>
  <div class="manual-page" lang="cs">
    <div class="manual-print-head">
      <h1>{{ $t('manual.printTitle') }}</h1>
      <p>{{ $t('manual.printLead') }}</p>
    </div>

    <header class="manual-toolbar">
      <h1>{{ $t('manual.title') }}</h1>
      <form class="manual-search" role="search" @submit.prevent="stepMatch(1)">
        <label class="sr-only" for="manual-query">{{ $t('manual.search') }}</label>
        <input
          id="manual-query"
          v-model="query"
          type="search"
          autocomplete="off"
          :placeholder="$t('manual.search')"
        />
        <button type="submit" class="manual-find" :disabled="!query" :title="$t('manual.search')">
          <i class="icon-magnifier" aria-hidden="true"></i>
        </button>
        <button type="button" :disabled="!query" :title="$t('manual.clear')" @click="query = ''">
          <i class="icon-close" aria-hidden="true"></i>
        </button>
        <button type="button" :disabled="!matchCount" :title="$t('manual.prev')" @click="stepMatch(-1)">
          <i class="icon-arrow-up" aria-hidden="true"></i>
        </button>
        <span v-if="matchCount" class="manual-match">{{ matchLabel }}</span>
        <button type="button" :disabled="!matchCount" :title="$t('manual.next')" @click="stepMatch(1)">
          <i class="icon-arrow-down" aria-hidden="true"></i>
        </button>
        <button type="button" class="manual-print" :title="$t('manual.print')" @click="printManual">
          <i class="icon-printer" aria-hidden="true"></i>
        </button>
      </form>
      <p class="manual-count">{{ countLabel }}</p>
      <button type="button" class="manual-toc-toggle" @click="tocOpen = !tocOpen">
        {{ tocOpen ? $t('manual.hideToc') : $t('manual.showToc') }}
      </button>
    </header>

    <div class="manual-layout">
      <nav
        v-show="tocOpen || wide"
        class="manual-toc"
        :aria-label="$t('manual.toc')"
      >
        <p class="manual-toc-label">{{ $t('manual.toc') }}</p>
        <a
          v-for="item in visibleToc"
          :key="item.id"
          href="#"
          :class="{ 'is-sub': item.level === 3, 'is-sub2': item.level === 4 }"
          @click.prevent="goTo(item.id)"
        >{{ item.title }}</a>
        <p v-if="!visibleToc.length" class="manual-empty">{{ $t('manual.nothing') }}</p>
      </nav>

      <article class="manual-content" ref="content" @click="onContentClick">
        <p v-if="!visibleSections.length" class="manual-empty">{{ $t('manual.nothing') }}</p>
        <section
          v-for="section in visibleSections"
          :key="section.id"
          class="manual-section"
        >
          <template v-for="(block, index) in section.blocks">
            <component
              v-if="block.type === 'heading'"
              :is="headingTag(block.level)"
              :id="block.id"
              :key="block.id"
              v-html="highlightHtml(escapeHtml(block.title), query)"
            ></component>
            <div
              v-else
              :key="section.id + '-html-' + index"
              class="manual-copy"
              v-html="highlightHtml(block.html, query)"
            ></div>
          </template>
        </section>
      </article>
    </div>
  </div>
</template>

<script>
import manualSource from "@/manual/manual.cs.md";
import { highlightHtml, normalizeText, parseManual } from "@/manual/render-manual";

const manualMd = typeof manualSource === "string" ? manualSource : (manualSource && manualSource.default) || "";
const manual = parseManual(manualMd);

export default {
  name: "Manual",
  data() {
    return {
      toc: manual.toc,
      sections: manual.sections,
      query: "",
      matchIndex: 0,
      matchCount: 0,
      tocOpen: false,
      wide: typeof window !== "undefined" && window.matchMedia("(min-width: 992px)").matches,
      queryBeforePrint: null
    };
  },
  computed: {
    tokens() {
      return normalizeText(this.query).split(/\s+/).filter(Boolean);
    },
    visibleSections() {
      if (!this.tokens.length) return this.sections;
      return this.sections.filter((section) => {
        const text = normalizeText(section.plain);
        return this.tokens.every((token) => text.indexOf(token) !== -1);
      });
    },
    visibleToc() {
      const ids = new Set(this.visibleSections.map((section) => section.id));
      return this.toc.filter((item) => ids.has(item.sectionId));
    },
    countLabel() {
      if (!this.tokens.length) return this.$t("manual.parts", { count: this.sections.length });
      return this.$t("manual.foundParts", { count: this.visibleSections.length });
    },
    matchLabel() {
      if (!this.matchCount) return "";
      return this.$t("manual.match", {
        current: this.matchIndex + 1,
        total: this.matchCount
      });
    }
  },
  watch: {
    query() {
      this.matchIndex = 0;
      this.refreshMatches();
    },
    visibleSections() {
      this.refreshMatches();
    }
  },
  mounted() {
    this.mq = window.matchMedia("(min-width: 992px)");
    this.onMedia = () => {
      this.wide = this.mq.matches;
    };
    this.onMedia();
    if (this.mq.addEventListener) this.mq.addEventListener("change", this.onMedia);
    else if (this.mq.addListener) this.mq.addListener(this.onMedia);
    this.onBeforePrint = () => {
      document.body.classList.add("manual-printing");
    };
    this.onAfterPrint = () => {
      this.afterPrint();
    };
    window.addEventListener("beforeprint", this.onBeforePrint);
    window.addEventListener("afterprint", this.onAfterPrint);
  },
  beforeDestroy() {
    if (this.mq && this.onMedia) {
      if (this.mq.removeEventListener) this.mq.removeEventListener("change", this.onMedia);
      else if (this.mq.removeListener) this.mq.removeListener(this.onMedia);
    }
    window.removeEventListener("beforeprint", this.onBeforePrint);
    window.removeEventListener("afterprint", this.onAfterPrint);
    document.body.classList.remove("manual-printing");
  },
  methods: {
    highlightHtml,
    escapeHtml(value) {
      return String(value)
        .replace(/&/g, "&amp;")
        .replace(/</g, "&lt;")
        .replace(/>/g, "&gt;");
    },
    headingTag(level) {
      if (level === 2) return "h2";
      if (level === 3) return "h3";
      return "h4";
    },
    onContentClick(event) {
      const link = event.target.closest ? event.target.closest("a[data-manual-id]") : null;
      if (!link) return;
      event.preventDefault();
      this.goTo(link.getAttribute("data-manual-id"));
    },
    goTo(id) {
      const el = document.getElementById(id);
      if (!el) return;
      el.scrollIntoView({ behavior: "smooth", block: "start" });
      if (!this.wide) this.tocOpen = false;
    },
    refreshMatches() {
      this.$nextTick(() => {
        const root = this.$refs.content;
        const marks = root ? root.querySelectorAll("mark") : [];
        this.matchCount = marks.length;
        if (this.matchIndex >= marks.length) this.matchIndex = 0;
        this.paintCurrent(marks);
        const current = marks[this.matchIndex];
        if (this.tokens.length && current && current.scrollIntoView) {
          current.scrollIntoView({ behavior: "smooth", block: "center" });
        }
      });
    },
    paintCurrent(marks) {
      const list = marks || (this.$refs.content ? this.$refs.content.querySelectorAll("mark") : []);
      for (let i = 0; i < list.length; i += 1) {
        if (i === this.matchIndex) list[i].classList.add("manual-mark-current");
        else list[i].classList.remove("manual-mark-current");
      }
    },
    stepMatch(direction) {
      if (!this.matchCount) return;
      const next = (this.matchIndex + direction + this.matchCount) % this.matchCount;
      this.matchIndex = next;
      const marks = this.$refs.content ? this.$refs.content.querySelectorAll("mark") : [];
      this.paintCurrent(marks);
      const current = marks[this.matchIndex];
      if (current && current.scrollIntoView) {
        current.scrollIntoView({ behavior: "smooth", block: "center" });
      }
    },
    printManual() {
      this.queryBeforePrint = this.query;
      this.query = "";
      this.$nextTick(() => {
        document.body.classList.add("manual-printing");
        window.print();
      });
    },
    afterPrint() {
      document.body.classList.remove("manual-printing");
      if (this.queryBeforePrint != null) {
        this.query = this.queryBeforePrint;
        this.queryBeforePrint = null;
      }
    }
  }
};
</script>

<style scoped>
.manual-page {
  display: flex;
  flex-direction: column;
  flex: 1 1 auto;
  min-height: 0;
  height: calc(100vh - 36px);
  max-height: calc(100vh - 36px);
  overflow: hidden;
  background: #fff;
  color: #23282c;
}
.manual-print-head,
.manual-print-toc {
  display: none;
}
.manual-toolbar {
  flex: 0 0 auto;
  display: flex;
  flex-wrap: wrap;
  align-items: center;
  gap: 6px;
  padding: 4px 8px;
  border-bottom: 1px solid #d8dce0;
  background: #fff;
  z-index: 2;
}
.manual-toolbar h1 {
  margin: 0;
  font-size: 0.875rem;
  font-weight: 600;
  line-height: 1.5;
  white-space: nowrap;
}
.manual-count,
.manual-toc-label {
  margin: 0;
  color: #73818f;
  font-size: 0.75rem;
}
.manual-search {
  display: flex;
  flex: 1 1 16rem;
  align-items: center;
  gap: 4px;
  margin: 0;
}
.manual-search input,
.manual-search button,
.manual-toc-toggle {
  font-size: 0.875rem;
  line-height: 1.5;
  padding: 0.25rem 0.5rem;
}
.manual-search input {
  flex: 1;
  min-width: 0;
  border: 1px solid #c8ced3;
  border-radius: 2px;
}
.manual-search button,
.manual-toc-toggle {
  border: 1px solid #c8ced3;
  background: #fff;
  color: #23282c;
  border-radius: 2px;
}
.manual-search .manual-print {
  background: #2f353a;
  color: #fff;
  border-color: #2f353a;
}
.manual-match {
  color: #73818f;
  font-size: 0.75rem;
  white-space: nowrap;
}
.manual-search button:disabled {
  opacity: 0.55;
}
.manual-toc-toggle {
  display: none;
}
.manual-layout {
  flex: 1 1 auto;
  min-height: 0;
  display: flex;
  align-items: stretch;
}
.manual-toc {
  flex: 0 0 240px;
  min-height: 0;
  overflow: auto;
  padding: 8px;
  border-right: 1px solid #d8dce0;
}
.manual-toc a {
  display: block;
  padding: 3px 0;
  color: #23282c;
  text-decoration: none;
}
.manual-toc a:hover {
  text-decoration: underline;
}
.manual-toc a.is-sub {
  padding-left: 12px;
  color: #4f5d73;
}
.manual-toc a.is-sub2 {
  padding-left: 24px;
  color: #5c6873;
}
.manual-content {
  flex: 1 1 auto;
  min-width: 0;
  min-height: 0;
  overflow: auto;
  padding: 12px 16px 32px;
}
.manual-section {
  scroll-margin-top: 12px;
}
.manual-content h2,
.manual-content h3 {
  scroll-margin-top: 12px;
}
.manual-content h2 {
  margin: 1.4rem 0 0.6rem;
  font-size: 1.35rem;
  padding-bottom: 0.2rem;
  border-bottom: 1px solid #d8dce0;
}
.manual-content h3 {
  margin: 1rem 0 0.4rem;
  font-size: 1.05rem;
}
.manual-content h4 {
  margin: 0.8rem 0 0.35rem;
  font-size: 1rem;
}
.manual-copy >>> p,
.manual-copy >>> li {
  margin-bottom: 0.35rem;
}
.manual-copy >>> ul,
.manual-copy >>> ol {
  padding-left: 1.2rem;
  margin-bottom: 0.6rem;
}
.manual-copy >>> blockquote {
  margin: 0.6rem 0;
  padding: 0.45rem 0.7rem;
  border-left: 4px solid #f9b115;
  background: #fff8e6;
}
.manual-copy >>> blockquote p {
  margin: 0;
}
.manual-copy >>> code {
  padding: 0 0.2rem;
  background: #f0f3f5;
}
.manual-copy >>> .manual-photo {
  margin: 0.75rem 0 1rem;
}
.manual-copy >>> .manual-photo img {
  max-width: 100%;
  height: auto;
  border: 1px solid #c8ced3;
  background: #fff;
}
.manual-copy >>> .manual-photo figcaption {
  margin-top: 4px;
  color: #73818f;
  font-size: 12px;
}
.manual-copy >>> mark,
.manual-content h2 >>> mark,
.manual-content h3 >>> mark,
.manual-content h4 >>> mark {
  background: #fff3a0;
  padding: 0;
}
.manual-content >>> mark.manual-mark-current {
  background: #f9b115;
  outline: 2px solid #d39e00;
}
.manual-empty {
  color: #73818f;
}
.sr-only {
  position: absolute;
  width: 1px;
  height: 1px;
  padding: 0;
  margin: -1px;
  overflow: hidden;
  clip: rect(0, 0, 0, 0);
  border: 0;
}
@media (max-width: 991.98px) {
  .manual-toc-toggle {
    display: inline-block;
  }
  .manual-toc {
    flex: 0 1 40%;
    max-height: 40%;
    border-right: 0;
    border-bottom: 1px solid #d8dce0;
  }
  .manual-layout {
    flex-direction: column;
  }
}
</style>

<style>
body.manual-printing .app-header,
body.manual-printing .sidebar,
body.manual-printing .aside-menu {
  display: none !important;
}
body.manual-printing .app-body,
body.manual-printing .main,
body.manual-printing .main .container-fluid {
  margin: 0 !important;
  display: block !important;
  overflow: visible !important;
  height: auto !important;
  min-height: 0 !important;
}
body.manual-printing .manual-page {
  display: block !important;
  height: auto !important;
  max-height: none !important;
  overflow: visible !important;
}
body.manual-printing .manual-layout,
body.manual-printing .manual-content {
  display: block !important;
  height: auto !important;
  max-height: none !important;
  overflow: visible !important;
}
body.manual-printing .manual-toolbar,
body.manual-printing .manual-toc {
  display: none !important;
}
body.manual-printing .manual-print-head {
  display: block !important;
}
body.manual-printing .manual-photo,
body.manual-printing .manual-photo img {
  -webkit-print-color-adjust: exact;
  print-color-adjust: exact;
}
body.manual-printing .manual-section {
  break-inside: auto;
}
body.manual-printing .manual-section + .manual-section {
  break-before: page;
}
body.manual-printing .manual-content h2,
body.manual-printing .manual-content h3,
body.manual-printing .manual-content h4,
body.manual-printing .manual-copy table,
body.manual-printing .manual-photo {
  break-inside: avoid;
}
body.manual-printing .manual-content {
  max-width: none;
}
@page {
  margin: 14mm;
}
</style>
